import React, { useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';
import { ConfluenceStats } from '../../lib/confluence';
import { useDisplayMode } from '../../context/DisplayModeContext';
import { cn } from '../../lib/cn';
import { fmtPctEs } from '../../lib/format';
type SortKey = 'winRate' | 'resultPct' | 'profitFactor' | 'avgR' | 'count' | 'score';
const columns: {
  key: SortKey;
  label: string;
}[] = [{
  key: 'count',
  label: 'Operaciones'
}, {
  key: 'winRate',
  label: 'Win Rate'
}, {
  key: 'resultPct',
  label: 'Resultado'
}, {
  key: 'avgR',
  label: 'RR medio'
}, {
  key: 'profitFactor',
  label: 'Profit Factor'
}, {
  key: 'score',
  label: 'Puntuación'
}];
export function ConfluenceTable({
  rows,
  minSample
}: {
  rows: ConfluenceStats[];
  minSample: number;
}) {
  const {
    formatResult
  } = useDisplayMode();
  const [sort, setSort] = useState<SortKey>('score');
  const [asc, setAsc] = useState(false);
  const sorted = [...rows].sort((a, b) => {
    const read = (item: ConfluenceStats) => {
      const value = item[sort];
      return typeof value === 'number' ? value : -Infinity;
    };
    return asc ? read(a) - read(b) : read(b) - read(a);
  });
  if (!rows.length) {
    return <p className="rounded-xl border border-dashed border-line bg-surface/40 px-4 py-6 text-center text-[13px] text-ink-soft" data-id="element-247" data-mp="58lnvr2kx8dm">
        Aún no hay operaciones con las tres confirmaciones completas.
      </p>;
  }
  return <div className="overflow-x-auto rounded-xl border border-line" data-id="element-248" data-mp="3ri628h1a4fak">
      <table className="w-full min-w-[820px] border-collapse text-left" data-id="element-249" data-mp="3mpmp1l6i7m6a">
        <thead data-id="element-250" data-mp="3hqryty67ivc5">
          <tr className="border-b border-line bg-surface/50 text-[11px] uppercase tracking-wide text-ink-soft" data-id="element-251" data-mp="3hqa5b9axiv7x">
            <th className="px-3 py-2.5 font-medium" data-id="element-252" data-mp="3dpzpl4xc4qfv">Configuración</th>
            {columns.map(column => <th key={column.key} className="px-3 py-2.5 text-right font-medium" data-id="element-253" data-mp="38oseavvzh94g">
                <button type="button" onClick={() => {
              if (sort === column.key) setAsc(value => !value);else {
                setSort(column.key);
                setAsc(false);
              }
            }} className={cn('inline-flex items-center gap-1 hover:text-ink', sort === column.key && 'text-ink')} data-id="element-254" data-mp="33qqx6gi4bjvm">
                  {column.label}
                  {sort === column.key && (asc ? <ArrowUpIcon className="h-3 w-3" data-id="element-255" data-mp="2p6ee2ndz7906" /> : <ArrowDownIcon className="h-3 w-3" data-id="element-256" data-mp="3ctcrfc6su0tv" />)}
                </button>
              </th>)}
          </tr>
        </thead>
        <tbody data-id="element-257" data-mp="3hsfz74e5yl5j">
          {sorted.map(row => {
          const insufficient = row.count < minSample;
          return <tr key={row.key} className="border-b border-line text-[12.5px] last:border-0" data-id="element-258" data-mp="3328mz6er1yda">
                <td className="px-3 py-2.5" data-id="element-259" data-mp="380te8gc0x7pl">
                  <p className="font-medium" data-id="element-260" data-mp="3vsif4l2wv4i5">{row.label}</p>
                  <p className="mt-0.5 text-[11px] text-ink-soft" data-id="element-261" data-mp="3vqxjst8rvhj8">
                    Fiabilidad: {row.reliability}
                    {insufficient && ' · Muestra insuficiente para clasificar esta configuración'}
                  </p>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums" data-id="element-262" data-mp="1k4ls2eyypwbd">
                  {row.count}
                  <span className="ml-1 text-[11px] text-ink-soft" data-id="element-263" data-mp="1yszyt0zi24x7">({row.wins}G · {row.losses}P)</span>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums" data-id="element-264" data-mp="1txb4bc2c7e7r">{row.winRate.toFixed(1)} %</td>
                <td className={cn('px-3 py-2.5 text-right font-medium tabular-nums', row.resultPct >= 0 ? 'text-pos' : 'text-neg')} data-id="element-265" data-mp="167q8hsl34mh2">
                  {formatResult({
                pct: row.resultPct,
                money: row.resultMoney
              })}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums" data-id="element-266" data-mp="1b4yuy5d9nco4">{row.avgR.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums" data-id="element-267" data-mp="2ql7dhvvg378y">{row.profitFactor === null ? '—' : row.profitFactor.toFixed(2)}</td>
                <td className={cn('px-3 py-2.5 text-right tabular-nums', insufficient && 'text-ink-soft')} data-id="element-268" data-mp="2mj3y4w5nniie">
                  {insufficient ? '—' : row.score.toFixed(1)}
                </td>
              </tr>;
        })}
        </tbody>
      </table>
      <p className="border-t border-line bg-surface/30 px-3 py-2 text-[11px] text-ink-soft" data-id="element-269" data-mp="3qv0aeesn4rma">
        Resultado según el selector Dinero / Porcentaje. Muestra mínima aplicada: {minSample} operaciones. Media por operación de la configuración destacada: {fmtPctEs(sorted[0]?.avgPct ?? 0)}.
      </p>
    </div>;
}